import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  from,
  Observable,
  of,
  Subscription,
} from 'rxjs';
import {
  SelectedCountryInterface,
  Multicountry,
  MultiCountryDraftData,
  CountryInterface,
  LanguageInterface,
} from '../../../../../shared/interfaces/create-surveys.interface';
import { Constants as CreateSurveyConstants } from '../../../../../shared/constants/create-survey.constants';
import {
  MultiCountryFeasCpiData,
  ResultInterface,
} from './multi-country-draft.interface';
import { BuyerApiService } from '../../../../../shared/services/buyer-api/survey.service';
import { notifyMessage } from '../../../../../shared/constants/notify-message';
import { v4 as uuidv4 } from 'uuid';
import { ActivatedRoute, Router } from '@angular/router';
import { SURVEY_STATUS } from '../../../../../shared/constants/survey-status';
import {
  BuyerSurveyResponse,
  Qualification as QualificationPayload,
  SupplierResponseInterface,
  SurveyNoTargetingResponse,
  SurveyRequest,
} from '../../../../../shared/interfaces/survey.interface';
import { ModalService } from '@purespectrum1/ui/modal';
import {
  debounceTime,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
} from 'rxjs/operators';
import {
  CreateSurveyService,
  MultiCountryData,
} from '../../../../create-survey.service';
import { SurveyPayloadService } from '../../../../survey-payload.service';
import { Constants } from '../../../../../shared/constants/dashboard-constants';
import { Constants as HeaderConstants } from '../../../../layout/header/header.constants';
import { ChangeSurveyStatusModalComponent } from '../../../../../shared/ui/change-survey-status-modal/change-survey-status-modal.component';
import {
  CriteriaQuota,
  FeasibilityRangeQuota,
  FeasibilityResponse,
} from '../../../../../shared/interfaces/feasibility.interface';
import { FeasibilityService } from '../../../../services/feasibility/feasibility.service';
import { MultiCountryDraftService } from '../../../../services/multi-country-draft/multi-country-draft.service';
import { EventEmitterService } from '../../../../../shared/services/event-emitter/event-emitter.service';
import { Qualification } from '../../../../../shared/interfaces/qualification.interface';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { CommonService } from '../../../../../shared/services/common-service/common-service';
import { FeasibilityDataService as FeasibilityComponentService } from '../../../../services/feasibility/feasibility-data.service';
import { AuthService } from '../../../../../shared/services/auth/auth.service';
import { QualificationsService } from '../../../../services/qualification/qualifications.service';
import { SurveyMetaDataService } from '../../../../../shared/services/survey-meta-data/survey-meta-data.service';
import { UnitedStatesCanadaAlphabeticalSort } from './domain/united-state-canada-alphabetical-sort';
import { SharedDataService } from '../../../../../shared/services/shared-data-service/shared-data-service';
import { TruncateNumber } from '../../../../utils/truncate-number';

type FeasibilityResponsePayload = {
  response: FeasibilityResponse;
  quotaSet: Map<number, CriteriaQuota[]>;
};

@Component({
  selector: 'ps-ui-multi-country-draft',
  templateUrl: './multi-country-draft.component.html',
  styleUrls: ['./multi-country-draft.component.css'],
})
export class MultiCountryDraftComponent
  implements OnInit, OnChanges, OnDestroy
{
  public selectedCountries: SelectedCountryInterface[] = [];
  public multiCountryDraft: boolean = false;
  public surveyTitle: string = '';
  public expectedIr: number = 0;
  public clickBalancing: number = 0;
  public surveyCategoryCode: number = 0;
  public source: string = '';
  public expectedLoi: number = 0;
  public fieldTime: number = 0;
  public completesRequired: number = 0;
  public multiCountryDraftData: MultiCountryDraftData = {
    countryData: [],
  };
  public multiCountryDraftLocale: MultiCountryDraftData = {
    countryData: [],
  };
  public loader: boolean = false;
  public isDraftSaved: boolean = false;
  public qualifications!: Array<Qualification>;
  public payloadQualifications: QualificationPayload[] = [];
  public quotas: FeasibilityRangeQuota[] = [];
  public showLaunchAllButton: boolean = false;
  public surveyStatus = SURVEY_STATUS;
  public surveyId?: number;
  public surveyCpi: number = 0;
  public surveySupplier: SupplierResponseInterface[] = [];
  @Input() surveyDetails?: BuyerSurveyResponse;
  @Input() countries: CountryInterface[] = [];
  @Input() mcSurveyPayload = <SurveyRequest>{};
  @Input() mcSurveyAddType: string = '';
  @Input() isMultiCountryTemplate: boolean = false;
  public filteredCountries: CountryInterface[] = this.countries;
  public languages: LanguageInterface[] = [];
  @Output() readonly selectionChange = new EventEmitter();
  @Output() mcSurveyAdded = new EventEmitter();
  private _subscriptions: Subscription = new Subscription();
  public selectedCountry: CountryInterface = {} as CountryInterface;
  private _selectedLanguage: LanguageInterface = {} as LanguageInterface;
  public isCPILoading: boolean = false;
  public isMcSurveyLoading: boolean = false;
  public isSetHeaderCpi: boolean = false;
  public manualProcessing: boolean = false;
  public overrideEnable = this._auth.buyerConfig?.overridCmp;
  public offerPrice: number = 0;
  public buyerMargin: number = 0;
  public isServiceBuyerOperation: boolean = false;
  private _feasibilityDispatcher = new BehaviorSubject<[boolean, boolean]>([
    false,
    true,
  ]);
  private _preDraftFeasibilityDispatcher = new BehaviorSubject<boolean>(false);
  public switch: boolean = true;
  public runFeasCalcForSurveys: Array<number> = []; // Run feasibility calculations for only these surveys
  public shouldSetFeasibilityFromFlyout: boolean = true;
  private _isBpmRequired: boolean = false;
  private _isOperator: boolean = false;
  private _enableMultiCountry: boolean = false;
  private _enableMultiCountrySingleProjectView: boolean = false;

  constructor(
    private _createSurveyService: CreateSurveyService,
    private _toastr: ToasterService,
    private _buyerApiService: BuyerApiService,
    private _eventEmitterService: EventEmitterService,
    private _feasibilityService: FeasibilityService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _multiCountryDraftService: MultiCountryDraftService,
    private _commonService: CommonService,
    private _surveyPayloadService: SurveyPayloadService,
    private _auth: AuthService,
    private _modal: ModalService,
    private _feasibilityComponentService: FeasibilityComponentService,
    private _qualificationService: QualificationsService,
    private _surveyMetaDataService: SurveyMetaDataService,
    private _sharedDataService: SharedDataService
  ) {}

  ngOnInit(): void {
    this._isBpmRequired = !!this._auth.buyerConfig?.enablePMRequired;
    this._isOperator = this._auth.userType === 'operator';
    this.isServiceBuyerOperation = this._auth.isLoggedInAsServiceBuyer();
    this._enableMultiCountry = !!this._auth.buyerConfig?.enableMultiCountry;
    this._enableMultiCountrySingleProjectView =
      !!this._auth.buyerConfig?.enableMultiCountrySingleProjectView;
    this._subscriptions.add(
      this._createSurveyService.getMultiCountryData().subscribe((data) => {
        this.selectedCountries = data.selectedCountries;
        this.multiCountryDraft = data.isDraft;
        this.expectedIr = data.expectedIr;
        this.surveyTitle = data.surveyTitle;
        this.clickBalancing = data.clickBalancing;
        this.surveyCategoryCode = data.surveyCategoryCode;
        this.completesRequired = data.completesRequired;
        this.expectedLoi = data.expectedLoi;
        this.fieldTime = data.fieldTime;
        this.source = data.source;
        this.qualifications = data.qualifications;
        this.payloadQualifications =
          data.payloadQualifications as QualificationPayload[];
        this.quotas = data.quotas as FeasibilityRangeQuota[];
        this.offerPrice = data.offerPrice ? data.offerPrice : 0;
        this.buyerMargin = data.buyerMargin ? data.buyerMargin : 0;
        this._preDraftFeasibilityDispatcher.next(
          this.shouldSetFeasibilityFromFlyout
        );
      })
    );
    this._subscriptions.add(
      this._createSurveyService
        .getIsDraftSavedValue()
        .subscribe((val: boolean) => {
          this.isDraftSaved = val;
        })
    );

    // Kept at this position to subscribe feasibility run data before 'getMultiCountrySurveys' subscription
    this._subscriptions.add(
      this._multiCountryDraftService
        .getMultiCountryRunFeasibilityInfo()
        .subscribe((data) => {
          if (Object.keys(data).length) {
            this.shouldSetFeasibilityFromFlyout =
              data.shouldSetFeasibilityFromFlyout;
            this.runFeasCalcForSurveys = data.runFeasCalcForSurveys;
          }
        })
    );
    this._subscriptions.add(
      this._createSurveyService
        .getMultiCountrySurveys()
        .subscribe((countryData) => {
          countryData = this._orderCountry(countryData);
          if (countryData.length) {
            this.multiCountryDraftData = {
              countryData: countryData,
            };
            this._selectActiveDraftSurvey(this.surveyId);
            this._getCountries();
            this._feasibilityDispatcher.next([
              this.isSetHeaderCpi,
              this.shouldSetFeasibilityFromFlyout,
            ]);
          }
        })
    );
    this._subscriptions.add(
      this._activatedRoute.params?.subscribe((params) => {
        if (params['id']) {
          this.surveyId = Number(params['id']);
          this._createSurveyService.setIsDraftSavedValue(true);
          this._selectActiveDraftSurvey(this.surveyId);
        } else {
          this._createSurveyService.setIsDraftSavedValue(false);
        }
      })
    );
    if (this._router.url?.includes('/launch-survey')) {
      this.showLaunchAllButton = true;
    }

    this._subscriptions.add(
      this._multiCountryDraftService
        .getMultiCountryDraftData()
        .subscribe((multiCountryDraftData) => {
          this.multiCountryDraftLocale = multiCountryDraftData;
        })
    );

    this._subscriptions.add(
      this._surveyMetaDataService.getCPIData().subscribe((data) => {
        this.isCPILoading = data.loading;
        this._getManualProcessing();
      })
    );
    this._getManualProcessing();

    this._subscriptions.add(
      this._feasibilityDispatcher
        .pipe(debounceTime(1000))
        .subscribe(([isSetHeaderCpi, shouldSetFeasibilityFromFlyout]) => {
          if (this.isDraftSaved && shouldSetFeasibilityFromFlyout) {
            this.setFeasibility(isSetHeaderCpi);
          } else {
            // Resetting Multi country run feasibility info after a not-intended run
            this._multiCountryDraftService.setMultiCountryRunFeasibilityInfoDefault();
          }
        })
    );

    this._subscriptions.add(
      this._preDraftFeasibilityDispatcher
        .pipe(
          filter(
            (shouldSetFeasibilityFromFlyout) => shouldSetFeasibilityFromFlyout
          ),
          debounceTime(1000)
        )
        .subscribe(() => {
          this._manageMultiCountryLocale();
        })
    );

    this._subscriptions.add(
      this._multiCountryDraftService
        .getMultiCountryFeasCpiData()
        .subscribe((data) => {
          this._setCpiFeasibilityInFlyout(data);
          // Resetting Multi country run feasibility info after an intended and successful run
          this._multiCountryDraftService.setMultiCountryRunFeasibilityInfoDefault();
        })
    );

    this._subscriptions.add(
      this._createSurveyService
        .getIsManualProcessingPermission()
        .subscribe((isPermission: boolean) => {
          this.multiCountryDraftData.countryData.forEach((mcSurvey) => {
            if (this.manualProcessing && this.surveyId === mcSurvey.surveyId) {
              mcSurvey.manualProcessingLoading = !isPermission;
            }
          });
        })
    );

    this._subscriptions.add(
      this._createSurveyService
        .getProjectManagerForAllSurveys()
        .pipe(
          filter((bpm) => Object.keys(bpm).length > 0),
          map((bpm) => [
            {
              op: 'replace',
              path: `/bpm`,
              value: bpm.id,
            },
          ]),
          switchMap((payload) =>
            from(
              this.multiCountryDraftData.countryData.map((survey) => ({
                survey,
                payload,
              }))
            )
          ),
          filter(({ survey }) => survey.psSurveystatus === SURVEY_STATUS.DRAFT),
          mergeMap(({ survey, payload }) =>
            this._buyerApiService.patchSurvey(survey.surveyId!, payload)
          )
        )
        .subscribe()
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    /**
     * On template change, 'mcSurveyPayload' will surely change, but 'mcSurveyAddType' might not change
     * **Note** - this.multiCountryDraftData.countryData?.length is added as a caution to stop unnecessary
     * survey creations on open-close of MC flyout after template use
     */
    if (
      changes.mcSurveyPayload?.currentValue &&
      Object.keys(changes.mcSurveyPayload.currentValue).length &&
      this.multiCountryDraftData.countryData?.length &&
      !this.isMultiCountryTemplate
    ) {
      // Find out the index of survey to be deleted
      const deleteSurveyIndex = this._getIndexOfSurveyToDelete(
        this.mcSurveyPayload,
        this.mcSurveyAddType
      );
      this._deleteMcSurveyAndCreateNewOne(deleteSurveyIndex);
    }
  }

  private _getManualProcessing() {
    const processingSubscriber = this._surveyMetaDataService
      .getSurveyProcessing()
      .subscribe(({ manual }) => {
        if (typeof manual === 'boolean') {
          this.manualProcessing = manual;
        }
      });
    this._subscriptions.add(processingSubscriber);
  }

  // Order the countries by US => CA => the rest in alphabetical order
  private _orderCountry(listCountry: Multicountry[]) {
    return new UnitedStatesCanadaAlphabeticalSort(listCountry).sort();
  }

  private _pushSurveysParticipatingInFeasCalc(
    shouldCalculate: boolean,
    type: string = 'all',
    surveyId?: number
  ) {
    const shouldSetFeasibilityFromFlyout = shouldCalculate;
    let runFeasCalcForSurveys: Array<number> = []; // Run for all
    if (shouldCalculate && type === 'single' && surveyId) {
      runFeasCalcForSurveys = [Number(surveyId)];
    }
    this._multiCountryDraftService.setMultiCountryRunFeasibilityInfo({
      shouldSetFeasibilityFromFlyout,
      runFeasCalcForSurveys,
    });
  }

  private _getIndexOfSurveyToDelete(
    mcSurveyPayload: SurveyRequest,
    mcSurveyAddType: string
  ) {
    if (
      [
        CreateSurveyConstants.TEMPLATE_SELECTION_SCENARIOS_FOR_MC
          .SAME_LOCALE_AS_SELECTED,
        CreateSurveyConstants.TEMPLATE_SELECTION_SCENARIOS_FOR_MC
          .NOT_SAME_LOCALE_AS_SELECTED,
      ].includes(mcSurveyAddType)
    ) {
      return this.multiCountryDraftData.countryData.findIndex((mcSurvey) => {
        const mcSurveyLocalization = this._getMcSurveyLocalization(mcSurvey);
        return mcSurveyLocalization === mcSurveyPayload.survey_localization;
      });
    }
    return -1;
  }

  private _getMcSurveyLocalization(mcSurvey: Multicountry) {
    return `${mcSurvey.language.transalte_code}_${mcSurvey.short_Code}`;
  }

  private _deleteMcSurveyAndCreateNewOne(index: number) {
    if (index === -1) {
      this._createMcSurvey(this.mcSurveyPayload);
      return;
    }
    const mcSurvey = this.multiCountryDraftData.countryData[index];
    if (mcSurvey && mcSurvey.surveyId) {
      this._buyerApiService
        .deleteDraftSurvey(mcSurvey.surveyId)
        .subscribe(() => {
          this.multiCountryDraftData.countryData =
            this.multiCountryDraftData.countryData.filter(
              (data) => data.surveyId !== mcSurvey.surveyId
            );
          this._pushSurveysParticipatingInFeasCalc(false);
          this._createSurveyService.setMultiCountrySurveys(
            this.multiCountryDraftData.countryData
          );
          this._createMcSurvey(this.mcSurveyPayload, '', '', true);
        });
    }
  }

  private _getCountries() {
    if (!this.countries.length) {
      this._subscriptions.add(
        this._commonService.getCountries().subscribe(
          (response) => {
            this.countries = response;
            this._filterCountryList();
          },
          (error) => {
            this._toastr.error(error.error.msg);
          }
        )
      );
    } else {
      this._filterCountryList();
    }
  }

  private _filterCountryList() {
    if (this.multiCountryDraftData.countryData.length && this.isDraftSaved) {
      const existingCountries = this.multiCountryDraftData.countryData.map(
        (survey) => {
          return {
            countryCode: survey.short_Code,
            languageCode: survey.language.transalte_code,
          };
        }
      );
      this.filteredCountries = this.countries.filter((country) => {
        country.lang = country.lang.filter(
          (language) =>
            !existingCountries.find(
              (singleCountry) =>
                singleCountry.countryCode === country.short_Code &&
                singleCountry.languageCode === language.transalte_code
            )
        );
        return !!country.lang.length;
      });
    }
  }

  private _distributeSurveyCompletes(countryData: Multicountry[]) {
    if (
      this._enableMultiCountrySingleProjectView &&
      !this._enableMultiCountry
    ) {
      const completes = Math.floor(this.completesRequired / countryData.length);
      countryData.map((country) => (country.completesRequired = completes));
      let remainingCompletes =
        this.completesRequired -
        countryData.reduce((res, country) => {
          res += country.completesRequired;
          return res;
        }, 0);
      if (remainingCompletes > 0) {
        let countryIndex = 0;
        while (remainingCompletes > 0) {
          if (countryIndex >= countryData.length) {
            countryIndex = 0;
          }
          countryData[countryIndex].completesRequired++;
          countryIndex++;
          remainingCompletes--;
        }
      }
      return countryData;
    }
    return countryData;
  }

  private _manageMultiCountryLocale() {
    const countries = this.selectedCountries;
    if (countries?.length > 1 && this.multiCountryDraft && !this.isDraftSaved) {
      const countryData = [];
      for (const country of countries) {
        for (const lang of country.lang) {
          const multiCountryIndex =
            this.multiCountryDraftLocale.countryData.findIndex(
              (multiCountry) =>
                multiCountry.countryLanguageCode ===
                `${country.short_Code}-${lang.short_code}`
            );
          let checked = true;
          if (multiCountryIndex > -1) {
            checked =
              this.multiCountryDraftLocale.countryData[multiCountryIndex]
                .checked;
          }
          countryData.push({
            name: country.name,
            id: country.id,
            short_Code: country.short_Code,
            pl_code: country.pl_code,
            language: lang,
            checked: checked, // MC-17 By default all draft locales should be selected
            countryLanguageCode: `${country.short_Code}-${lang.short_code}`,
            expectedIr: this.expectedIr,
            expectedLoi: this.expectedLoi,
            supplier_ids: [],
            surveyTitle: this.surveyTitle,
            surveyCategoryCode: this.surveyCategoryCode,
            completesRequired: this.completesRequired,
            fieldTime: this.fieldTime,
            payloadQualifications: this.payloadQualifications,
            quotas: this.quotas,
            offerPrice: this.offerPrice,
            buyerMargin: this.buyerMargin,
            manualProcessingLoading: false,
          });
        }
      }
      const distributedCountryCompletes =
        this._distributeSurveyCompletes(countryData);
      const countryDataSorted = this._orderCountry(distributedCountryCompletes);
      this.multiCountryDraftData = {
        countryData: countryDataSorted,
      };
      this._setMultiCountryTotalCompletesOnHeader();
      this._pushSurveysParticipatingInFeasCalc(true, 'all');
      this.setFeasibility();
    }
  }

  async setFeasibility(isSetHeaderCpi = false) {
    let multiCountryData: Multicountry[] = [];

    if (this.runFeasCalcForSurveys.length) {
      multiCountryData = this.multiCountryDraftData.countryData.filter(
        (mcSurvey) =>
          mcSurvey.surveyId &&
          this.runFeasCalcForSurveys.includes(mcSurvey.surveyId)
      );
    } else {
      multiCountryData = Object.assign(
        multiCountryData,
        this.multiCountryDraftData.countryData
      );
    }
    const data = await this._setMultiCountryFeasibility(
      multiCountryData,
      isSetHeaderCpi
    );

    // data is not 'undefined' only when surveys' drafts are not saved
    if (data) {
      this.multiCountryDraftData.countryData = data;
      // When CPI/Feasibility data for each draft locale is loaded, loading Avg CPI and Total Feasbility in the top bar
      this._multiCountryDraftService.setMultiCountryDraftData(
        this.multiCountryDraftData
      );
    }
    // Resetting Multi country run feasibility info after an intended and successful run
    this._multiCountryDraftService.setMultiCountryRunFeasibilityInfoDefault();
  }

  private async _setMultiCountryFeasibility(
    multiCountryData: Multicountry[],
    isSetHeaderCpi = false
  ) {
    // When draft surveys are saved
    if (this.isDraftSaved) {
      this._markMcSurveyLoading(true);
      await this._setFeasibilityPostDraftSave(multiCountryData);
      this._markMcSurveyLoading(false);
      return;
    }
    // When draft surveys are not saved;
    this._markMcSurveyLoading(true);
    multiCountryData = await this._setFeasibilityBeforeDraftSave(
      multiCountryData,
      isSetHeaderCpi
    );
    this._markMcSurveyLoading(false);
    return multiCountryData;
  }

  async _setFeasibilityBeforeDraftSave(
    multiCountryData: Multicountry[],
    isSetHeaderCpi: boolean
  ) {
    const feasibilityRequestPromises: Promise<FeasibilityResponsePayload>[] =
      [];
    multiCountryData.forEach((element) => {
      const feasibilityRequestData = this._mapPayload(element);
      const qualification = this._qualificationService.getQualifications();
      const { request: requestPayload, quotaSet } =
        this._feasibilityService.buildFeasibilityPayload(
          qualification,
          feasibilityRequestData,
          element?.surveyId,
          element.supplier_ids
        );
      if (requestPayload) {
        delete requestPayload.project_id;
      }
      feasibilityRequestPromises.push(
        this._feasibilityService
          .getFeasibility(requestPayload)
          .pipe(map((response) => ({ response, quotaSet })))
          .toPromise()
      );
    });
    let results: PromiseSettledResult<FeasibilityResponsePayload>[] =
      await Promise.allSettled(feasibilityRequestPromises);
    let actualCpiCount = 0;
    const multiPriceOverRide = multiCountryData.filter(
      (condata) => !condata.priceOverride
    );
    for (let i = 0; i < results.length; i++) {
      const index = this._findMultiCountryIndex(
        multiCountryData[i],
        multiCountryData
      );
      if (index === -1) {
        continue;
      }
      const result = results[index];

      if (result.status == 'fulfilled') {
        const { response, quotaSet } = result.value;
        if (!multiCountryData[index].priceOverride) {
          multiCountryData = this._setCpiBeforeDraftSave(
            response,
            index,
            multiCountryData
          );
          actualCpiCount++;
        }
        response.max_feasibility =
          this._feasibilityService.calculateTotalFeasibility(response);
        multiCountryData[index].currency_symbol = response?.currency_fx?.symbol;
        multiCountryData[index].manualProcessingLoading = false;
        multiCountryData[index].feasibility = Math.min(
          response.total_feasibility,
          response.max_feasibility
        );
      }
    }
    if (multiPriceOverRide.length == actualCpiCount && !isSetHeaderCpi) {
      this.isSetHeaderCpi = true;
    }
    return multiCountryData;
  }

  private async _setFeasibilityPostDraftSave(multiCountryData: Multicountry[]) {
    multiCountryData.forEach(async (mcSurvey) => {
      // In case of manual processing, if data is not saved, do not proceed further for that survey
      if (
        this.manualProcessing &&
        this.isCPILoading &&
        mcSurvey.manualProcessingLoading
      ) {
        return;
      }
      const [surveyId, language, surveyRequest, qualifications] =
        this._buildPayloadForSetFeasibility(mcSurvey);
      await this._feasibilityComponentService.setFeasibility(
        surveyId,
        language,
        surveyRequest,
        qualifications,
        true,
        !!mcSurvey.active
      );
    });
  }

  private _buildPayloadForSetFeasibility(mcSurvey: Multicountry) {
    const surveyId = Number(mcSurvey.surveyId);
    const language = mcSurvey.language.short_code;
    const surveyRequest = this._mapPayload(mcSurvey);
    const qualifications = this._qualificationService.getQualifications();
    return [surveyId, language, surveyRequest, qualifications];
  }

  private _markMcSurveyLoading(loading: boolean) {
    if (this.isDraftSaved) {
      this.isMcSurveyLoading = loading;
    }
  }

  private _findMultiCountryIndex(
    multiCountry: Multicountry,
    multiCountryData: Multicountry[] = []
  ) {
    return multiCountryData.findIndex(
      (mcSurvey: Multicountry) =>
        this._getMcSurveyLocalization(mcSurvey) ===
        this._getMcSurveyLocalization(multiCountry)
    );
  }

  private _setCpiFeasibilityInFlyout(mcFeasCpiData: MultiCountryFeasCpiData) {
    // When CPI and feasibility is calculated in the feasibility service,
    // updating CPI and feasibility data in the flyout as well
    this.multiCountryDraftData.countryData.forEach((mcSurvey, index) => {
      if (Number(mcSurvey.surveyId) === Number(mcFeasCpiData.surveyId)) {
        const { cpi, feasibility, currency_symbol, buyerData, priceOverride } =
          mcFeasCpiData;
        const { completesRequired, expected_ir, expected_loi, field_time } =
          buyerData;
        mcSurvey = {
          ...mcSurvey,
          cpi,
          feasibility,
          currency_symbol,
          completesRequired,
          expectedIr: expected_ir,
          expectedLoi: expected_loi,
          fieldTime: field_time,
          manualProcessingLoading: false,
          ...((priceOverride || priceOverride === 0) && { priceOverride }),
        };
        this.multiCountryDraftData.countryData[index] = mcSurvey;
      }
    });
  }

  private _setCpiBeforeDraftSave(
    feasibilityResponse: FeasibilityResponse,
    index: number,
    multiCountryData: Multicountry[]
  ) {
    if (!multiCountryData[index]) {
      return multiCountryData;
    }
    const mcData = multiCountryData[index];
    if (!mcData.surveyId) {
      if (this.isServiceBuyerOperation) {
        multiCountryData[index].cpi = Number(mcData.offerPrice || 0);
      } else if (feasibilityResponse?.feasibility_cpi) {
        multiCountryData[index].cpi = feasibilityResponse?.feasibility_cpi || 0;
      } else {
        multiCountryData[index].cpi = feasibilityResponse?.recommended_cpi || 0;
      }
    }
    return multiCountryData;
  }

  private _mapPayload(singleMultiCountryData: Multicountry) {
    const { id: buyer_user_id, cmp: buyer_company } = this._auth.user!;
    const isServiceBuyerOperation = this._auth.isLoggedInAsServiceBuyer();

    let payload = {
      survey_title: singleMultiCountryData.surveyTitle,
      click_balancing: 0,
      survey_category_code: singleMultiCountryData.surveyCategoryCode,
      survey_localization: `${singleMultiCountryData.language.transalte_code}_${singleMultiCountryData.short_Code}`,
      completes_required: singleMultiCountryData.completesRequired,
      expected_ir: singleMultiCountryData.expectedIr,
      expected_loi: singleMultiCountryData.expectedLoi,
      field_time: singleMultiCountryData.fieldTime,
      source: 'platform',
      buyer_user_id,
      buyer_company: isServiceBuyerOperation
        ? this._auth.companyConfig.id!
        : buyer_company,
    };

    if (singleMultiCountryData.payloadQualifications?.length) {
      payload = {
        ...payload,
        ...{ qualifications: singleMultiCountryData.payloadQualifications },
      };
    }

    if (singleMultiCountryData.quotas?.length) {
      payload = { ...payload, ...{ quotas: singleMultiCountryData.quotas } };
    }

    return payload;
  }

  public openMultiCountryDraftModal() {
    this.selectionChange.emit();
  }

  public selectMultiCountryDraft(event: Event, index: number) {
    if ((<HTMLInputElement>event.target).checked) {
      this.multiCountryDraftData.countryData[index].checked = true;
    } else {
      this.multiCountryDraftData.countryData[index].checked = false;
    }
    this._multiCountryDraftService.setMultiCountryDraftData(
      this.multiCountryDraftData
    );
    this._setMultiCountryTotalCompletesOnHeader();
  }

  public selectAllMultiCountry() {
    this.multiCountryDraftData.countryData.forEach(
      (_multiCountry: Multicountry, index: number) => {
        this.multiCountryDraftData.countryData[index].checked = true;
      }
    );
    this._multiCountryDraftService.setMultiCountryDraftData(
      this.multiCountryDraftData
    );
    this._setMultiCountryTotalCompletesOnHeader();
  }

  public deSelectAllMultiCountry() {
    this.multiCountryDraftData.countryData.forEach(
      (_multiCountry: Multicountry, index: number) => {
        this.multiCountryDraftData.countryData[index].checked = false;
      }
    );
    this._multiCountryDraftService.setMultiCountryDraftData(
      this.multiCountryDraftData
    );
    this._setMultiCountryTotalCompletesOnHeader();
  }

  private _setMultiCountryTotalCompletesOnHeader(
    showTotalCompletes: boolean = !!this.selectedCountries?.length
  ) {
    showTotalCompletes =
      this._enableMultiCountrySingleProjectView && this._enableMultiCountry
        ? false
        : showTotalCompletes;
    const totalCompleteLabel =
      this._enableMultiCountrySingleProjectView && !this._enableMultiCountry
        ? HeaderConstants.COMPLETES
        : HeaderConstants.TOTAL_COMPLETES;
    const totalCompletes = this.multiCountryDraftData.countryData
      .filter((country) => country.checked)
      .reduce((total, country) => (total += country.completesRequired), 0);
    this._createSurveyService.setMultiCountryTotalCompletes({
      showTotalCompletes,
      totalCompletes,
      totalCompleteLabel,
    });
  }

  public deleteSurveyDraft(multiCountry: Multicountry, index: number) {
    const surveyDeleteData = {
      surveyId: multiCountry.surveyId,
      toDelete: true,
      isMc: true,
    };

    const modalRef = this._modal.open(ChangeSurveyStatusModalComponent, {
      data: surveyDeleteData,
      width: '500px',
      height: '21rem',
    });

    modalRef.onClose$
      .pipe(filter((msg) => msg === Constants.MODAL_MESSAGE.DELETE_SURVEY))
      .subscribe(() => {
        this.multiCountryDraftData.countryData = this._orderCountry(
          this.multiCountryDraftData.countryData
        );
        this.multiCountryDraftData = {
          countryData: this.multiCountryDraftData.countryData.filter(
            (data) => data.surveyId != multiCountry.surveyId
          ),
        };
        this._pushSurveysParticipatingInFeasCalc(false);
        this._createSurveyService.setMultiCountrySurveys(
          this.multiCountryDraftData.countryData
        );
        this._toastr.success('Survey removed and deleted successfully');

        if (this.multiCountryDraftData.countryData.length === 0) {
          this._router.navigate(['/dashboard']);
        } else {
          const newSelected = this._findIndexOfSurveyToSelectPostDelete(index);
          this.selectDraftSurvey(newSelected, true);
        }
      });
  }

  private _findIndexOfSurveyToSelectPostDelete(formerIndex: number) {
    // When manual Processing is enabled and CPI/feasibility is still loading (not clicked on 'Save & Process' yet)
    let newIndex = -1;
    if (this.manualProcessing && this.isCPILoading) {
      newIndex = this.multiCountryDraftData.countryData.findIndex(
        (mcSurvey) => mcSurvey.manualProcessingLoading
      );
    }
    if (newIndex !== -1) {
      return newIndex;
    }
    return formerIndex > this.multiCountryDraftData.countryData.length - 1
      ? 0
      : formerIndex;
  }

  public saveSurveyDraft() {
    this.loader = true;
    const countryData = this.multiCountryDraftData.countryData;
    const checkedCoundryData = countryData.filter(
      (mulitCountry: { checked: boolean }) => mulitCountry.checked === true
    );
    if (checkedCoundryData.length > 0) {
      let surveysPromises: any = [];
      let rejectedSurveys: string[] = [];
      const uniqueId = uuidv4();
      const { id: buyer_user_id, cmp: buyer_company } = this._auth.user!;
      checkedCoundryData.forEach((localeData, index) => {
        let payload = {
          survey_title: `${this.surveyTitle} - MC-${localeData.short_Code}-${localeData.language.transalte_code}`,
          click_balancing: this.clickBalancing ? this.clickBalancing : 0,
          survey_category_code: this.surveyCategoryCode,
          survey_localization: `${localeData.language.transalte_code}_${localeData.short_Code}`,
          completes_required: localeData.completesRequired,
          expected_ir: this.expectedIr,
          expected_loi: this.expectedLoi,
          field_time: this.fieldTime,
          source: this.source,
          mc_unique_id: uniqueId,
          mc_parent: index === 0, // set first mc as parent
          buyer_user_id,
          buyer_company,
          qualifications: localeData.payloadQualifications || [],
          quotas: localeData.quotas || [],
        };
        if (this.isServiceBuyerOperation && localeData.buyerMargin) {
          const serviceOperatorPayload = {
            margin_override: { enable: true, margin: localeData.buyerMargin },
          };
          payload = { ...payload, ...serviceOperatorPayload };
        }
        if (localeData.offerPrice) {
          payload = {
            ...payload,
            ...{ offer_price: Number(localeData.offerPrice) },
          };
        }
        const addSurvey = this._buyerApiService
          .addSurvey(payload as SurveyRequest)
          .toPromise();
        surveysPromises.push(addSurvey);
      });

      const createdSurveys: Multicountry[] = [];
      Promise.allSettled(surveysPromises).then((results: any) => {
        results.forEach((result: ResultInterface, index: number) => {
          if (result.status == 'rejected') {
            rejectedSurveys.push(
              `${checkedCoundryData[index].name}-${checkedCoundryData[index].language.name}`
            );
          } else if (result.status == 'fulfilled') {
            checkedCoundryData[index].surveyId = result.value?.ps_survey_id;
            checkedCoundryData[index].psSurveystatus =
              result.value?.ps_survey_status;
            createdSurveys.push(checkedCoundryData[index]);
          }
        });
        // MC-97 : Add automatically in "exclude list" the survey with same country different language
        this._updateExcludeListOfSurveys(createdSurveys);
        this._createSurveyService.setMultiCountryData({} as MultiCountryData);
        this._setMultiCountryTotalCompletesOnHeader(false);
        this.multiCountryDraftData.countryData = createdSurveys;
        this._createSurveyService.setIsDraftSavedValue(true);
        this._pushSurveysParticipatingInFeasCalc(false);
        this._createSurveyService.setMultiCountrySurveys(createdSurveys);
        this.loader = false;
        if (rejectedSurveys.length) {
          this._toastr.error(
            `${
              notifyMessage.errorMessage.MULTICOUNTRY_DRAFT.REJECTED_SURVEYS
            } ${rejectedSurveys.join(',')}`
          );
        } else {
          this._toastr.success(
            notifyMessage.successMessage.MULTICOUNTRY_DRAFT.SAVED_SUCCESS
          );
        }
        if (createdSurveys.length) {
          const selectedDraftSurveyIndex = 0; // select first survey as default
          this.selectDraftSurvey(selectedDraftSurveyIndex, false);
        }
        this.loader = false;
      });
    } else {
      this._toastr.error(
        notifyMessage.warningMessage.MULTICOUNTRY_DRAFT.SELECT_MULTICOUNTRY
      );
      this.loader = false;
    }
  }

  private _updateExcludeListOfSurveys(surveys: Multicountry[]) {
    const map1 = new Map<number, number[]>();
    surveys
      .filter((survey) => survey.surveyId)
      .forEach((survey) => {
        const sameCountrySurveys = surveys
          .filter(
            (s) =>
              s.short_Code === survey.short_Code &&
              s.surveyId !== survey.surveyId
          )
          .map((s) => s.surveyId)
          .map(Number);
        if (sameCountrySurveys.length && survey.surveyId) {
          map1.set(survey.surveyId, sameCountrySurveys);
        }
      });
    const promises = [];
    for (const [surveyId, surveyGrouping] of map1) {
      const payload = [
        {
          op: 'replace',
          path: '/survey_grouping',
          value: surveyGrouping,
        },
      ];
      const patchRequest = this._buyerApiService
        .patchSurvey(surveyId, payload)
        .toPromise();
      promises.push(patchRequest);
    }
    if (promises.length) {
      Promise.all(promises)
        .then((r) => {
          console.log(r);
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }

  public async launchAllSurveyBtnClick() {
    const surveysPromises: any = [];

    const draftSurveys = this.multiCountryDraftData.countryData.filter(
      (survey) => survey.psSurveystatus === SURVEY_STATUS.DRAFT
    );

    const bpm = await this._createSurveyService
      .getProjectManagerForAllSurveys()
      .pipe(take(1))
      .toPromise();
    if (this._isBpmRequired && !bpm.id) {
      return this._toastr.error(
        this._modifyStringForBuyerRole(
          notifyMessage.errorMessage.SURVEY_LAUNCH.BPM_REQUIRED_ON_LAUNCH
        )
      );
    }

    await this._surveyUpdate(draftSurveys);

    draftSurveys.forEach((curretSurvey) => {
      if (curretSurvey.surveyId) {
        surveysPromises.push(
          this._buyerApiService
            .updateSurveyStatus(curretSurvey.surveyId, {
              ps_survey_status: SURVEY_STATUS.LIVE,
            })
            .toPromise()
        );
      }
    });
    Promise.allSettled(surveysPromises).then((results: any) => {
      let isAllSurveysLaunched = true;
      results.forEach((currentResult: any, index: number) => {
        if (currentResult.status == 'rejected') {
          this._toastr.error(
            notifyMessage.errorMessage.SURVEY_LAUNCH.LIVE_URL_ERR_FROM_API ==
              currentResult.reason.error.ps_api_response_message
              ? `${notifyMessage.errorMessage.SURVEY_LAUNCH.EMPTY_LIVE_URL} to ${draftSurveys[index].countryLanguageCode} Survey`
              : `Survey ${draftSurveys[index].countryLanguageCode} - ${currentResult.reason.error.ps_api_response_message}`
          );
          isAllSurveysLaunched = false;
        } else if (currentResult.status == 'fulfilled') {
          const launchedSurveyIndex =
            this.multiCountryDraftData.countryData.findIndex(
              (survey) => survey.surveyId === draftSurveys[index].surveyId
            );
          this.multiCountryDraftData.countryData[
            launchedSurveyIndex
          ].psSurveystatus = SURVEY_STATUS.LIVE;
        }
      });
      if (isAllSurveysLaunched) {
        this._toastr.success(
          notifyMessage.successMessage.MULTICOUNTRY_DRAFT.LAUNCH_SUCCESS
        );
        this._router.navigateByUrl('/dashboard');
        this.showLaunchAllButton = false;
      }
    });
  }

  private _modifyStringForBuyerRole(string: string) {
    if (this._isOperator) {
      return string.replace(/Project Manager/g, 'Buyer Project Manager');
    }

    return string;
  }

  private async _surveyUpdate(draftSurveys: Multicountry[]) {
    const [url, bpm, billing_id] = await combineLatest([
      this._createSurveyService.getLiveUrlForAllSurveys().pipe(take(1)),
      this._createSurveyService.getProjectManagerForAllSurveys().pipe(take(1)),
      this._createSurveyService.getBillingSurveyPoForAllSurveys().pipe(take(1)),
    ]).toPromise();

    if (url.live_url === '') {
      return;
    }

    const promises = draftSurveys.map((survey: any) => {
      const _url = [
        {
          op: 'replace',
          path: `/live_url`,
          value: url.live_url,
        },
      ];

      const _po =
        billing_id.length > 0
          ? [
              {
                op: 'replace',
                path: '/billing_id',
                value: billing_id,
              },
            ]
          : [];

      const _bpm =
        Object.keys(bpm).length > 0
          ? [
              {
                op: 'replace',
                path: `/bpm`,
                value: bpm.id,
              },
            ]
          : [];

      return this._buyerApiService
        .patchSurvey(survey.surveyId!, [..._url, ..._bpm, ..._po])
        .toPromise();
    });

    return Promise.all(promises);
  }

  public selectDraftSurvey(index: number, afterDelete = false) {
    let isManualProcessingStoppage = false;
    if (!afterDelete && this.mcSurveyAddType === '') {
      isManualProcessingStoppage =
        this._handleManualProcessBrowsingAnotherLocale();
    }
    if (isManualProcessingStoppage) {
      return;
    }
    this._activatePageLoader();
    if (this.isDraftSaved) {
      const selectedDraftSurvey = this.multiCountryDraftData.countryData[index];
      if (selectedDraftSurvey && selectedDraftSurvey.surveyId) {
        this._selectActiveDraftSurvey(selectedDraftSurvey.surveyId);
        const subdraftSub = this._eventEmitterService.draftIsSaved
          .pipe(take(1))
          .subscribe(() => {
            if (this._router.url?.includes('/launch-survey')) {
              this._router.navigateByUrl(
                `/launch-survey/${selectedDraftSurvey.surveyId}`
              );
            } else {
              this._router.navigateByUrl(
                `/surveys/${selectedDraftSurvey.surveyId}/edit`
              );
            }
          });
        this._subscriptions.add(subdraftSub);

        const [proceed, surveyPayload$] = this._handlePostSurveyDeleteScenarios(
          afterDelete,
          selectedDraftSurvey.surveyId
        );
        if (!proceed) {
          return;
        }
        surveyPayload$.subscribe((survey) => {
          this._eventEmitterService.onMultiCountrySelectionChange(survey);
        });
      }
    } else {
      this._deSelectAllDraftSurveys();
    }
  }

  private _handleManualProcessBrowsingAnotherLocale() {
    if (this.manualProcessing && this.isCPILoading) {
      this._toastr.warning(
        notifyMessage.warningMessage.MULTICOUNTRY
          .MANUAL_PROCESSING_BROWSE_ANOTHER_COUNTRY
      );
      return true;
    }
    return false;
  }

  private _activatePageLoader() {
    if (this.manualProcessing && this.isDraftSaved) {
      this._sharedDataService.setIsPageLoaderDisplay(true);
    }
  }

  private _handlePostSurveyDeleteScenarios(
    afterDelete: boolean,
    selectedSurveyId: number
  ): [boolean, Observable<any>] {
    if (!afterDelete) {
      return [true, of(undefined)];
    }
    const isSameSurveySelected =
      Number(selectedSurveyId) === Number(this.surveyId);
    // If post delete, it is different survey selected, then we can safely trigger router navigation
    if (!isSameSurveySelected) {
      this._eventEmitterService.draftIsSavedEvent();
      return [false, of(undefined)];
    }
    /** If it is same survey selected post delete, then router navigation won't detect it and hence new survey
     * data (with updated survey grouping, for example) needs to be fetched separately
     */
    const surveyPayload$ = this._buyerApiService
      .getSurveyById(selectedSurveyId)
      .pipe(
        map((response) => this._surveyPayloadService.mapSurveyDetails(response))
      );
    return [true, surveyPayload$];
  }

  private _deSelectAllDraftSurveys() {
    this.multiCountryDraftData.countryData.forEach((mcSurvey) => {
      mcSurvey.active = false;
    });
  }

  private _selectActiveDraftSurvey(surveyId?: number) {
    if (surveyId) {
      this.multiCountryDraftData.countryData.forEach((survey) => {
        survey.active = survey?.surveyId === surveyId;
      });
    }
  }

  public selectCountry(country: CountryInterface) {
    this.selectedCountry = country;
    this.languages = country.lang;
    this.setLanguage(this.languages[0]);
  }

  public setLanguage(language: LanguageInterface) {
    this._selectedLanguage = language;
  }

  public async addNewSurvey() {
    if (!this.selectedCountry.short_Code || !this._selectedLanguage) {
      this._toastr.error(notifyMessage.errorMessage.COUNTRY_MISSING);
      return;
    }

    const isManualProcessingStoppage =
      this._handleManualProcessBrowsingAnotherLocale();
    if (isManualProcessingStoppage) {
      return;
    }

    const [existingSurvey] = this.multiCountryDraftData.countryData;
    const surveyTitle =
      this._multiCountryDraftService.extractBaseSurveyTitleFromMcSurveyTitle(
        existingSurvey
      );

    const { id: buyer_user_id, cmp: buyer_company } = this._auth.user!;
    let payload = {
      survey_title: `${surveyTitle} - MC-${this.selectedCountry.short_Code}-${this._selectedLanguage.transalte_code}`,
      click_balancing: Number(!!this.surveyDetails?.click_balancing),
      survey_category_code: existingSurvey.surveyCategoryCode,
      survey_localization: `${this._selectedLanguage.transalte_code}_${this.selectedCountry.short_Code}`,
      completes_required: existingSurvey.completesRequired,
      expected_ir: existingSurvey.expectedIr,
      expected_loi: existingSurvey.expectedLoi,
      field_time: existingSurvey.fieldTime,
      source: 'platform',
      mc_unique_id: this.surveyDetails?.mc_unique_id,
      buyer_user_id,
      buyer_company,
    };
    if (this.isServiceBuyerOperation && existingSurvey.buyerMargin) {
      const serviceOperatorPayload = {
        margin_override: { enable: true, margin: existingSurvey.buyerMargin },
      };
      payload = { ...payload, ...serviceOperatorPayload };
    }
    if (existingSurvey.offerPrice) {
      payload = {
        ...payload,
        ...{ offer_price: Number(existingSurvey.offerPrice) },
      };
    }
    this._createMcSurvey(
      payload,
      this.selectedCountry.name,
      this._selectedLanguage.name
    );
  }

  private _createMcSurvey(
    payload: SurveyRequest,
    countryName: string = '',
    languageName: string = '',
    afterDelete: boolean = false
  ) {
    const [isLocaleExist, existingSurvey] =
      this._checkIfLocaleAlreadyExistsInMcSurveys(payload);
    if (isLocaleExist) {
      this._toastr.error(
        notifyMessage.errorMessage.MULTICOUNTRY.LOCALE_ALREADY_EXISTS.replace(
          '<LOCALE>',
          `${existingSurvey.name}-${existingSurvey.language.transalte_code}`
        )
      );
      return;
    }
    this.loader = true;
    this._activatePageLoader();
    this._buyerApiService.addSurvey(payload).subscribe(
      (newSurvey) => {
        this.isSetHeaderCpi = false;
        this._sendSurveyInMcConfiguration(newSurvey);
        this._selectMcSurveyAndResetValues(newSurvey, afterDelete);
        this.loader = false;
        this.mcSurveyAdded.emit();
        this._toastr.success(
          notifyMessage.successMessage.MULTICOUNTRY_DRAFT.NEW_SURVEY_ADDED
        );
      },
      (err) => {
        this._toastr.error(
          countryName && languageName
            ? `${notifyMessage.errorMessage.MULTICOUNTRY_DRAFT.REJECTED_SURVEYS} ${countryName}-${languageName}`
            : err?.error?.ps_api_response_message
        );
        this.loader = false;
      }
    );
  }

  private _checkIfLocaleAlreadyExistsInMcSurveys(
    payload: SurveyRequest
  ): [boolean, Multicountry] {
    const index = this.multiCountryDraftData.countryData.findIndex(
      (mcSurvey) =>
        this._getMcSurveyLocalization(mcSurvey) === payload.survey_localization
    );
    if (index !== -1) {
      return [true, this.multiCountryDraftData.countryData[index]];
    }
    return [false, {} as Multicountry];
  }

  private _sendSurveyInMcConfiguration(newSurvey: SurveyNoTargetingResponse) {
    const mappedSurvey = this._createSurveyService.mapMultiCouuntrySurveyData([
      newSurvey,
    ] as BuyerSurveyResponse[]);
    // On launch-survey page, if new survey is attempted to be added, then allowing feasibility call, so that data can reach flyout
    if (this._router.url?.includes('/launch-survey')) {
      this._pushSurveysParticipatingInFeasCalc(
        true,
        'single',
        Number(newSurvey.ps_survey_id)
      );
    } else {
      this._pushSurveysParticipatingInFeasCalc(false);
    }
    this._createSurveyService.setMultiCountrySurveys([
      ...this.multiCountryDraftData.countryData,
      ...mappedSurvey,
    ]);
  }

  private _selectMcSurveyAndResetValues(
    newSurvey: SurveyNoTargetingResponse,
    afterDelete: boolean = false
  ) {
    const newSurveyIndex = this.multiCountryDraftData.countryData.findIndex(
      (survey) => survey.surveyId === newSurvey.ps_survey_id
    );
    this.selectDraftSurvey(newSurveyIndex, afterDelete);
    this.selectedCountry = {} as CountryInterface;
    this.languages = [];
  }

  updateMultiCountryisEdit(multiCountry: Multicountry) {
    if (this.isDraftSaved) {
      multiCountry.isEdit = true;
      multiCountry.initialCpi = multiCountry.cpi;
    }
  }

  setMulticountrySurveyCpi(multiCountry: Multicountry) {
    const selectedDraftSurveyIndex =
      this.multiCountryDraftData.countryData.findIndex(
        (survey) => survey?.surveyId === Number(multiCountry.surveyId)
      );
    this.multiCountryDraftData.countryData[selectedDraftSurveyIndex].cpi =
      this.multiCountryDraftData.countryData[
        selectedDraftSurveyIndex
      ].initialCpi;
    multiCountry.isEdit = false;
    multiCountry.showConfirmationBox = false;
  }

  confirmOvervalue(multiCountry: Multicountry) {
    this.updateCurrentCpi(multiCountry, false);
    multiCountry.showConfirmationBox = false;
  }

  updateCurrentCpi(multiCountry: Multicountry, showConfirmbox: boolean = true) {
    const cpi = multiCountry.cpi ? multiCountry.cpi : 0;
    const isConfirmboxVisible = this.verifyConfirmBox(
      multiCountry,
      showConfirmbox
    );
    if (isConfirmboxVisible) {
      this._toastr.error(notifyMessage.CPI_CONFIRMATION_ERROR);
      return;
    }
    const selectedDraftSurveyIndex =
      this.multiCountryDraftData.countryData.findIndex(
        (survey) => survey?.surveyId === Number(multiCountry.surveyId)
      );

    this.multiCountryDraftData.countryData[selectedDraftSurveyIndex].cpi =
      Number(cpi);
    this.multiCountryDraftData.countryData[
      selectedDraftSurveyIndex
    ].priceOverride = Number(cpi);
    const surveyId = multiCountry.surveyId ? multiCountry.surveyId : 0;
    this._buyerApiService
      .updateOfferPriceOfSurvey(surveyId, cpi)
      .subscribe(async (response) => {
        this._pushSurveysParticipatingInFeasCalc(true, 'single', surveyId);
        this._createSurveyService.setMultiCountrySurveys(
          this.multiCountryDraftData.countryData
        );
        multiCountry.isEdit = false;
      });
  }

  verifyConfirmBox(multiCountry: Multicountry, showConfirmbox: boolean = true) {
    const cpi = multiCountry.cpi ? multiCountry.cpi : 0;
    multiCountry.showConfirmationBox = false;
    if (
      cpi > Number(CreateSurveyConstants.MULTICOUNTRY_MAX_CONFIRMATION_CPI) &&
      showConfirmbox
    ) {
      multiCountry.showConfirmationBox = true;
      return true;
    }
    return false;
  }

  onCpiChange(event: string, multiCountry: Multicountry) {
    const cpi = new TruncateNumber(event).places(2);
    multiCountry.cpi = cpi as unknown as number;
    this.verifyConfirmBox(multiCountry);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
